import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { SupabaseService } from '../services/supabaseService';
import { Inventory, Profile } from '../types';
import { Coins, Shield, Scroll, RotateCcw, ShieldAlert } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TudorHeader } from '../components/TudorHeader';

const PIECE_PRICES: Inventory = {
    p: 1, n: 3, b: 3, r: 5, q: 9, k: 10
};

const PIECE_NAMES: Record<keyof Inventory, string> = {
    p: 'Infantry', n: 'Cavalry', b: 'Clergy', r: 'Tower', q: 'Lady', k: 'Lord'
};

const PIECE_ICONS: Record<keyof Inventory, string> = {
    p: '♟\uFE0E', n: '♞', b: '♝', r: '♜', q: '♛', k: '♚'
};

const PILLAR_VISUAL_MAX = 200; // 200 units = 100% visual height

// --- PILLAR COMPONENT ---
// Optimized with Memo to prevent unnecessary re-renders of non-active pillars (though balancing updates them anyway, it helps with diffing)
interface PillarProps {
    type: keyof Inventory;
    count: number;
    price: number;
    maxPossibleCount: number;
    scaleMax: number;
    onUpdate: (type: keyof Inventory, newCount: number) => void;
    reserveCount: number;
}

const Pillar = React.memo(({ type, count, price, maxPossibleCount, scaleMax, onUpdate, reserveCount }: PillarProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartCount, setDragStartCount] = useState(0);

    const onPointerDown = (e: React.PointerEvent) => {
        setIsDragging(true);
        setDragStartCount(count);
        if (containerRef.current) {
            containerRef.current.setPointerCapture(e.pointerId);
        }
        handleInteraction(e.clientY);
    };

    const handleInteraction = (clientY: number) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();

        const heightPx = rect.bottom - clientY;
        const percent = Math.max(0, Math.min(1, heightPx / rect.height));

        const currentScaleMax = Math.max(PILLAR_VISUAL_MAX, scaleMax);
        const targetCount = Math.floor(percent * currentScaleMax);

        const finalCount = Math.min(targetCount, maxPossibleCount);

        if (finalCount !== count) {
            onUpdate(type, finalCount);
        }
    };

    const onPointerMove = (e: React.PointerEvent) => {
        if (isDragging) {
            e.preventDefault();
            handleInteraction(e.clientY);
        }
    };

    const onPointerUp = (e: React.PointerEvent) => {
        setIsDragging(false);
        if (containerRef.current) {
            containerRef.current.releasePointerCapture(e.pointerId);
        }
    };

    const effectiveScaleMax = Math.max(PILLAR_VISUAL_MAX, scaleMax);
    const fillPercent = Math.min(100, (count / effectiveScaleMax) * 100);
    const isCapped = count >= maxPossibleCount && maxPossibleCount > 0;
    const delta = count - dragStartCount;

    return (
        <div className="flex flex-col items-center group select-none relative w-full h-[300px] lg:w-32 lg:h-[550px] pt-4 lg:pt-12">

            {/* The Pillar Track (Interactive Area) */}
            <div
                ref={containerRef}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerLeave={onPointerUp}
                className="relative flex-grow w-full cursor-ns-resize flex flex-col justify-end items-center z-10 touch-none"
            >
                {/* 1. Guideline */}
                <div className="absolute inset-x-0 bottom-0 top-0 border-x border-zinc-800/30 mx-8 pointer-events-none"></div>
                <div className="absolute bottom-0 w-px h-full bg-zinc-800/30 pointer-events-none"></div>

                {/* 2. Active Pillar */}
                <div
                    className={`relative w-12 bg-zinc-950 border-x border-tudor-gold/50 transition-none ease-out flex flex-col justify-start overflow-hidden shadow-[0_0_20px_rgba(0,0,0,0.5)] ${isCapped && isDragging ? 'border-red-500/50' : ''}`}
                    style={{ height: `${fillPercent}%`, minHeight: '0px' }}
                >
                    <div className="absolute inset-0 flex justify-between px-2 opacity-30 pointer-events-none">
                        <div className="w-px h-full bg-tudor-gold"></div>
                        <div className="w-px h-full bg-tudor-gold"></div>
                    </div>
                </div>

                {/* 3. The Capital */}
                {/* REMOVED translateY(50%) to prevent hiding behind base */}
                <div
                    className={`absolute w-20 h-20 transition-none ease-out z-10 flex flex-col items-center justify-end pb-2 ${isCapped && isDragging ? 'translate-y-[1px]' : ''}`}
                    style={{ bottom: `${fillPercent}%` }}
                >
                    {/* DELTA POPUP (Only while dragging) */}
                    {isDragging && delta !== 0 && (
                        <div className={`absolute -top-12 text-sm font-bold font-mono px-2 py-0.5 rounded border shadow-lg backdrop-blur-sm animate-fade-in-up
                            ${delta > 0 ? 'bg-green-900/80 border-green-500 text-green-200' : 'bg-red-900/80 border-red-500 text-red-200'}
                        `}>
                            {delta > 0 ? '+' : ''}{delta}
                        </div>
                    )}

                    {/* Snap Tension Indicator */}
                    {isCapped && isDragging && (
                        <div className="absolute -inset-4 border border-red-500/30 rounded-full animate-ping pointer-events-none"></div>
                    )}

                    {/* The Icon */}
                    <div className={`text-5xl transition-transform duration-200 drop-shadow-[0_4px_4px_rgba(0,0,0,0.8)] ${count > 0 ? 'text-tudor-gold drop-shadow-[0_0_15px_rgba(197,160,89,0.4)]' : 'text-zinc-700 group-hover:text-zinc-500'}`}>
                        {PIECE_ICONS[type]}
                    </div>
                </div>

            </div>

            {/* INTEGRATED BASE */}
            <div className="w-full lg:w-32 h-[80px] lg:h-[100px] bg-gradient-to-t from-zinc-950 to-zinc-900 border-t border-zinc-800 flex flex-col items-center justify-center z-30 shadow-2xl shrink-0 mt-2 pointer-events-none">

                {/* BIG COUNT NUMBER */}
                <div className={`text-2xl lg:text-4xl font-mono font-bold transition-colors mb-1 ${count > 0 ? 'text-tudor-gold drop-shadow-md' : 'text-zinc-700'}`}>
                    {count}
                </div>

                {/* Name */}
                <div className="text-[8px] lg:text-[10px] font-serif uppercase tracking-[0.2em] text-zinc-400 mb-1">{PIECE_NAMES[type]}</div>

                {/* Metadata Row */}
                <div className="flex items-center gap-1 lg:gap-3 mt-1">
                    <div className="flex items-center gap-1 text-[14px] lg:text-[20px] text-zinc-400 font-mono bg-black/50 px-2 py-0.5 rounded border border-zinc-800">
                        <Coins className="text-tudor-gold w-3 h-3 lg:w-5 lg:h-5" /> {price}g
                    </div>
                    {/*<div className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest flex flex-col items-center leading-none">
                        <div className="flex items-center gap-1"><Scroll size={10} /> Reserves</div>
                        <div className="text-zinc-300 text-xs">{reserveCount}</div>
                    </div>*/}
                </div>
            </div>

        </div>
    );
});


export const Barracks: React.FC = () => {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [profile, setProfile] = useState<Profile | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const fetchProfile = async () => {
        try {
            const p = await SupabaseService.getProfile();
            setProfile(p);
        } catch (error) {
            console.error("Failed to load profile", error);
            // Fallback so it doesn't infinite load if Supabase fails
            setProfile({
                id: 'error_user',
                username: 'Unknown Commander',
                rating: 1000,
                gold: 0,
                is_premium: false,
                ranked_eligible: false,
                active_ranked_wars: 0,
                campaigns_completed: 0,
                consec_forfeits: 0,
                templates: [],
                inventory: { p: 8, n: 2, b: 2, r: 2, q: 1, k: 1 }
            } as Profile);
        }
    };

    useEffect(() => {
        if (!loading) fetchProfile();
    }, [loading]);

    if (loading || !profile) return (
        <div className="min-h-screen bg-black flex items-center justify-center text-tudor-gold">
            <div className="animate-pulse tracking-widest uppercase text-xs">Entering Royal Armory...</div>
        </div>
    );

    return (
        <div className="min-h-screen bg-tudor-black text-white font-sans selection:bg-tudor-gold selection:text-black overflow-hidden flex flex-col">
            <TudorHeader title="Barracks" />

            <BarracksContent
                profile={profile}
                setProfile={setProfile}
                fetchProfile={fetchProfile}
                isProcessing={isProcessing}
                setIsProcessing={setIsProcessing}
            />
        </div>
    );
};

// Extracted Content to separate Profile null check complexity
const BarracksContent: React.FC<{
    profile: Profile;
    setProfile: React.Dispatch<React.SetStateAction<Profile | null>>;
    fetchProfile: () => Promise<void>;
    isProcessing: boolean;
    setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ profile, setProfile, fetchProfile, isProcessing, setIsProcessing }) => {

    // Target Inventory represents the Desired State
    const [targetInventory, setTargetInventory] = useState<Inventory>({ ...profile.inventory });

    // Standard Army Definition
    const STANDARD_ARMY: Inventory = { p: 8, n: 2, b: 2, r: 2, q: 1, k: 1 };

    // Initialize Target (Pre-fill Standard if Weak)
    useEffect(() => {
        const pawnCount = profile.inventory.p || 0;
        const hasKing = (profile.inventory.k || 0) > 0;

        // If army is critically weak (unplayable), default to Standard Army for convenience
        if (pawnCount < 4 || !hasKing) {
            setTargetInventory(STANDARD_ARMY);
        } else {
            setTargetInventory({ ...profile.inventory });
        }
    }, [profile]); // Only on profile load/refresh

    // Derived Financials
    const getRefundPrice = (type: string) => Math.max(1, PIECE_PRICES[type as keyof Inventory] - 3);

    const calculateNetCost = (current: Inventory, baseline: Inventory) => {
        let cost = 0;
        (Object.keys(current) as Array<keyof Inventory>).forEach(k => {
            const diff = current[k] - (baseline[k] || 0);
            if (diff > 0) {
                if (k === 'k' && (baseline.k || 0) === 0) {
                    cost += 1 + (diff - 1) * PIECE_PRICES.k;
                } else {
                    cost += diff * PIECE_PRICES[k];
                }
            } else if (diff < 0) {
                cost += diff * getRefundPrice(k);
            }
        });
        return cost;
    };

    let netCost = calculateNetCost(targetInventory, profile.inventory);
    let projectedGold = profile.gold - netCost;

    // Debt Surcharge Calculation (Levy rises as loan increases)
    let levySurcharge = 0;
    if (projectedGold < 0) {
        // If we are in debt, the "Levy" (Interest/Fee) applies to the deficit
        // E.g. Deficit is 500g. Surcharge is 50g.
        // Total Cost increases by 50g.
        // New Projected Gold = Old Projected - Surcharge.
        levySurcharge = Math.floor(Math.abs(projectedGold) * 0.10);
        projectedGold -= levySurcharge;

        // Update Net Cost for display
        netCost += levySurcharge;
    }

    // Debt Limit: 0g (if solvent) or -10,000g (if already in debt/broke)
    const debtLimit = profile.gold > 0 ? 0 : -10000;
    const isValid = projectedGold >= debtLimit;

    // Use location to check for redirect context
    const location = useLocation();
    const navigate = useNavigate();

    const handleConfirm = async () => {
        if (!isValid) return;
        setIsProcessing(true);
        try {
            await SupabaseService.updateRoster(targetInventory, projectedGold);
            // Optimistic Update locally
            setProfile(prev => prev ? ({ ...prev, inventory: targetInventory, gold: projectedGold }) : null);

            // Redirect Logic
            const returnUrl = (location.state as any)?.returnUrl;
            if (returnUrl) {
                navigate(returnUrl);
            } else {
                navigate('/court');
            }

        } catch (e: any) {
            console.error(e);
            if (e.message === "No user") {
                setProfile(prev => prev ? ({ ...prev, inventory: targetInventory, gold: projectedGold }) : null);

                // Redirect Logic
                const returnUrl = (location.state as any)?.returnUrl;
                if (returnUrl) {
                    navigate(returnUrl);
                } else {
                    navigate('/court');
                }
            } else {
                alert("Roster update failed.");
                // Revert
                setTargetInventory({ ...profile.inventory });
            }
        } finally {
            setIsProcessing(false);
        }
    };

    // Stable Max Calc (Dynamic based on current selection budget)
    const getMaxForType = useCallback((type: keyof Inventory) => {
        if (type === 'q') return targetInventory['k'] || 0;
        if (type === 'k') return 999;

        // If we have debt capacity (-10000), we can buy until projectedGold hits -10000.
        // Remaining Budget = ProjectedGold - (-10000) = ProjectedGold + 10000
        // But we must account for the 10% surcharge on new spending if already negative?
        // Approximation: Budget available ~ (ProjectedGold + 10000) / 1.1 (if in debt)

        const price = PIECE_PRICES[type];
        if (price === 0) return 999;

        const effectiveBudget = projectedGold - debtLimit;
        if (effectiveBudget <= 0) return targetInventory[type]; // Maxed out

        // If currently positive, we can spend 'Gold' normally, then '10000' with tax.
        // Simplified: Just use raw budget limit. Surcharge is small enough that 'Try/Fail' or just 'Close Enough' is fine.
        // Or strict:
        let affordable = 0;
        if (projectedGold >= 0) {
            affordable += Math.floor(projectedGold / price);
            // Plus debt capacity
        }

        // Let's just use a loose upper bound to prevent infinite loops, checking actual isValid in render.
        let rawAffordable = Math.floor(effectiveBudget / price);

        if (type === 'k' as keyof Inventory && (profile.inventory.k || 0) === 0 && targetInventory.k === 0) {
            // First king is 1, remaining budget goes to kings at 10
            if (effectiveBudget >= 1) {
                rawAffordable = 1 + Math.floor((effectiveBudget - 1) / 10);
            } else {
                rawAffordable = 0;
            }
        }

        return targetInventory[type] + rawAffordable;
    }, [projectedGold, targetInventory, debtLimit]);

    // HIGH WATER MARK SCALING
    // Tracks the maximum 'maxPossibleCount' seen so far to prevent pillars "growing" (zooming in) when budget drops.
    const [maxScales, setMaxScales] = useState<Record<string, number>>({});

    useEffect(() => {
        setMaxScales(prev => {
            const next = { ...prev };
            let changed = false;
            (Object.keys(PIECE_PRICES) as Array<keyof Inventory>).forEach(k => {
                const currentMax = getMaxForType(k);
                if (currentMax > (next[k] || 0)) {
                    next[k] = currentMax;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [projectedGold, targetInventory, getMaxForType]);

    // Update Logic
    const updateRoster = useCallback((targetType: keyof Inventory, targetCount: number) => {
        setTargetInventory(prev => {
            return { ...prev, [targetType]: targetCount };
        });
    }, []);

    if (!profile) return null;

    // Changes List
    const changes: { type: keyof Inventory; diff: number; cost: number }[] = [];
    (Object.keys(targetInventory) as Array<keyof Inventory>).forEach(k => {
        const diff = targetInventory[k] - (profile.inventory[k] || 0);
        if (diff !== 0) {
            let cost = 0;
            if (diff > 0) {
                if (k === 'k' && (profile.inventory.k || 0) === 0) {
                    cost = 1 + (diff - 1) * PIECE_PRICES.k;
                } else {
                    cost = diff * PIECE_PRICES[k];
                }
            } else {
                cost = diff * getRefundPrice(k);
            }
            changes.push({ type: k, diff, cost });
        }
    });

    return (
        <div className="flex-grow flex flex-col lg:flex-row h-full max-w-[1600px] max-h-fit mx-auto w-full p-6 gap-8">
            <div className="flex-grow flex flex-col relative z-0">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-zinc-900/30 via-zinc-950 to-black pointer-events-none -z-10"></div>
                <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-end border-b border-zinc-800 pb-4 shrink-0">
                    <div>
                        <h2 className="text-2xl lg:text-3xl font-serif text-tudor-gold tracking-widest uppercase flex items-center gap-3">
                            <Shield className="fill-tudor-gold/10" /> Royal Commission
                        </h2>
                        <p className="text-zinc-500 text-sm mt-2 max-w-xl">
                            Drag pillars to Recruit (Up) or Disband (Down).
                        </p>
                    </div>
                    <div className="text-left lg:text-right">
                        <div className="text-[10px] uppercase text-zinc-500 tracking-wider">Treasury Projection</div>
                        <div className={`text-2xl font-mono ${projectedGold < 0 ? 'text-red-500' : 'text-tudor-gold'}`}>
                            {projectedGold.toLocaleString()}g
                        </div>
                    </div>
                </div>

                <div className="flex-grow grid grid-cols-3 gap-x-2 gap-y-4 lg:flex lg:items-end lg:justify-center lg:gap-8 px-4 relative pb-8">
                    {(Object.keys(PIECE_PRICES) as Array<keyof Inventory>).map(key => {
                        const currentPrice = key === 'k' && (profile.inventory.k || 0) === 0 && targetInventory.k === 0 ? 1 : PIECE_PRICES[key];
                        return (
                            <Pillar
                                key={key}
                                type={key}
                                count={targetInventory[key]}
                                price={currentPrice}
                                maxPossibleCount={getMaxForType(key)}
                                scaleMax={maxScales[key] || getMaxForType(key)}
                                onUpdate={updateRoster}
                                reserveCount={profile.inventory?.[key] || 0}
                            />
                        );
                    })}
                </div>
            </div>

            {/* RIGHT: LEDGER SIDEBAR */}
            <div className="w-full lg:w-80 flex-shrink-0 flex flex-col gap-6 z-10">
                {/* TREASURY */}
                <div className={`p-0.5 rounded shadow-2xl shrink-0 bg-gradient-to-br ${netCost > 0 ? 'from-[#b48e3d] to-[#8a6d2f]' : (netCost < 0 ? 'from-green-900 to-green-800' : 'from-zinc-800 to-zinc-900')}`}>
                    <div className="bg-black/90 p-6 flex flex-col items-center">
                        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-400 mb-2">Net Transaction</div>
                        <div className={`text-3xl font-mono font-bold flex items-center gap-3 ${netCost > 0 ? 'text-red-400' : (netCost < 0 ? 'text-green-400' : 'text-white')}`}>
                            <Coins size={24} className={netCost > 0 ? 'text-red-400' : (netCost < 0 ? 'text-green-400' : 'text-zinc-500')} />
                            {netCost > 0 ? `-${netCost}` : `+${Math.abs(netCost)}`}
                        </div>
                    </div>
                </div>



                {/* RECEIPT */}
                <div className="bg-zinc-950 border border-zinc-800 rounded flex-grow flex flex-col overflow-hidden shadow-2xl relative">
                    <div className="p-4 border-b border-zinc-800 bg-zinc-900/50 flex items-center gap-2 shrink-0">
                        <Scroll size={16} className="text-zinc-500" />
                        <span className="text-sm font-serif uppercase tracking-widest text-zinc-300">
                            Pending Orders
                        </span>
                    </div>

                    <div className="flex-grow p-4 space-y-2 overflow-y-auto">
                        {changes.map((c) => (
                            <div key={c.type} className="flex justify-between items-center text-sm border-b border-zinc-800/50 pb-2">
                                <span className={`font-bold flex items-center gap-2 ${c.diff > 0 ? 'text-green-500' : 'text-red-500'}`}>
                                    {c.diff > 0 ? '+' : ''}{c.diff} {PIECE_NAMES[c.type]}
                                </span>
                                <span className="text-zinc-500 font-mono">
                                    {c.cost > 0 ? `-${c.cost}g` : `+${Math.abs(c.cost)}g`}
                                </span>
                            </div>
                        ))}
                        {changes.length === 0 && <div className="text-center text-zinc-700 italic py-8">No changes pending.</div>}

                        {/* DEBT SURCHARGE ITEM */}
                        {levySurcharge > 0 && (
                            <div className="flex justify-between items-center text-sm border-t border-red-900/50 pt-2 mt-2">
                                <span className="font-bold flex items-center gap-2 text-red-500 uppercase text-[10px] tracking-wider">
                                    <ShieldAlert size={12} /> Royal Levy Surcharge
                                </span>
                                <span className="text-red-500 font-mono">
                                    -{levySurcharge}g
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="p-6 bg-zinc-900 border-t border-tudor-gold/30 shrink-0">
                        <div className="flex justify-between items-center mb-4 text-sm">
                            <span className="text-zinc-400">Final Balance</span>
                            <span className={`font-mono ${projectedGold < 0 ? 'text-red-500' : 'text-tudor-gold'}`}>
                                {projectedGold.toLocaleString()}g
                            </span>
                        </div>

                        <button
                            onClick={handleConfirm}
                            disabled={!isValid || changes.length === 0 || isProcessing}
                            className={`w-full py-4 uppercase tracking-[0.2em] font-bold text-sm transition-all border ${!isValid || changes.length === 0
                                ? 'bg-zinc-900 border-zinc-800 text-zinc-700 cursor-not-allowed'
                                : 'bg-black border-tudor-gold text-tudor-gold hover:bg-tudor-gold hover:text-black shadow-[0_0_15px_rgba(197,160,89,0.3)]'
                                }`}
                        >
                            {isValid ? (isProcessing ? "Sealing..." : "Confirm Orders") : (debtLimit === 0 ? "Insufficient Funds" : "Debt Limit Exceeded")}
                        </button>

                        <button
                            onClick={() => setTargetInventory({ ...profile.inventory })}
                            className="w-full mt-3 flex items-center justify-center gap-2 text-zinc-600 hover:text-white text-xs uppercase tracking-wider transition-colors"
                        >
                            <RotateCcw size={10} /> Reset
                        </button>
                    </div>
                </div>
            </div>

        </div>
    );
};
